import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useToast } from '~libs/toast';
import { getErrorMessage } from '~libs/axiosWrapper';
import { useExchangeTokenMutation } from '~direct-debits/actions/oauth';
import { connectionsApi } from '~direct-debits/actions/connections';
import { useAppDispatch } from 'hooks';

export type OAuthCallbackStatus = 'access_denied' | 'oauth_error' | 'missing_params' | 'error' | 'loading';

export interface UseOAuthCallbackFlowResult {
  status: OAuthCallbackStatus;
  errorMessage: string;
  handleBack: () => void;
}

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  server_error: 'GoCardless is temporarily unavailable. Please try again in a moment.',
  temporarily_unavailable: 'GoCardless is temporarily unavailable. Please try again in a moment.',
  invalid_request: 'The authorization request was invalid. Please try connecting again.',
  unauthorized_client: 'TurnPoint is not authorized for this operation. Please contact support.',
  unsupported_response_type: 'The authorization request was invalid. Please try connecting again.',
  invalid_scope: 'The authorization request was invalid. Please try connecting again.',
};

export function useOAuthCallbackFlow(): UseOAuthCallbackFlowResult {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const [exchangeToken, { isError, error }] = useExchangeTokenMutation();
  const hasExchanged = useRef(false);

  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const oauthError = searchParams.get('error');

  const handleBack = () => navigate('/direct-debits', { replace: true });

  useEffect(() => {
    if (hasExchanged.current) return;
    if (oauthError || !code || !state) return;

    hasExchanged.current = true;
    exchangeToken({ code, state })
      .unwrap()
      .then(() => {
        dispatch(connectionsApi.util.invalidateTags([{ type: 'connections', id: 'LIST' }]));
        showToast('success', 'GoCardless account connected successfully');
        navigate('/direct-debits', { replace: true });
      })
      .catch(() => {
        // Error surface handled via isError branch in the result
      });
  }, [code, state, oauthError, exchangeToken, dispatch, showToast, navigate]);

  if (oauthError === 'access_denied') {
    return {
      status: 'access_denied',
      errorMessage: 'You declined the GoCardless authorization request.',
      handleBack,
    };
  }

  if (oauthError) {
    return {
      status: 'oauth_error',
      errorMessage: OAUTH_ERROR_MESSAGES[oauthError] ?? `GoCardless returned an error: ${oauthError}`,
      handleBack,
    };
  }

  if (!code || !state) {
    return {
      status: 'missing_params',
      errorMessage: 'Invalid callback — missing authorization code or state parameter.',
      handleBack,
    };
  }

  if (isError) {
    return { status: 'error', errorMessage: getErrorMessage(error), handleBack };
  }

  return { status: 'loading', errorMessage: '', handleBack };
}
